import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { AuthService as AuthApiService, DefaultService, JWTTokenResponse, RolesService, UserCredentials } from 'app/services';
// import { AuthenticationService } from 'app/services/api/authentication.service';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import jwt_decode, { jwtDecode } from "jwt-decode";

@Injectable({providedIn: 'root'})
export class AuthService
{
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private authService = inject(AuthApiService);

    // private _authenticationService = inject(AuthenticationService);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem("accessToken", token);
    }
    
    get accessToken(): string {
        return localStorage.getItem("accessToken") ?? "";
    }

    set refreshToken(token: string) {
        localStorage.setItem("refreshToken", token);
    }
    get refreshToken(): string | null {
        return localStorage.getItem("refreshToken");
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param username
     */
    forgotPassword(username: string): Observable<any>
    {
        return this._httpClient.post('api/auth/forgot-password', username);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {   
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }

        var userCredentials: UserCredentials = {
            Email: credentials.email,
            Password: credentials.password
        };

        return this.authService.login(userCredentials).pipe(
            switchMap((response: JWTTokenResponse) => {
                // Store the access token in the local storage
                this.accessToken = response.AccessToken;
                this.refreshToken = response.RefreshToken;
                // Set the authenticated flag to true
                this._authenticated = true;
                
                // Store the user in the local storage
                // localStorage.setItem('user', JSON.stringify(response.identity));
                // this._userService.user = response.identity;

                // this.rolesService.getRoles().subscribe((roles) => {
                //     this._userService.user.roleId = roles.find(role => role.name === this._userService.user.roleId)?.id;
                // }

                //TODO: Handle user data and role in login response.
                //DEV: Currently testing UI by manually setting RoleId.
                this._userService.user = {
                    id: response.UserInfo.Id,
                    email: credentials.email,
                    roleId: response.UserInfo.RoleLevel,
                    roleName: response.UserInfo.RoleName,
                    customerId: response.UserInfo.CustomerId,
                };

                // var test = jwtDecode(this.accessToken);
                
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        this._authenticated = true;
        return of(true);
        // // Sign in using the token
        // return this._httpClient.post('api/auth/sign-in-with-token', {
        //     accessToken: this.accessToken,
        // }).pipe(
        //     catchError(() =>

        //         // Return false
        //         of(false),
        //     ),
        //     switchMap((response: any) =>
        //     {
        //         // Replace the access token with the new one if it's available on
        //         // the response object.
        //         //
        //         // This is an added optional step for better security. Once you sign
        //         // in using the token, you should generate a new one on the server
        //         // side and attach it to the response object. Then the following
        //         // piece of code can replace the token with the refreshed one.
        //         if ( response.accessToken )
        //         {
        //             this.accessToken = response.accessToken;
        //         }

        //         // Set the authenticated flag to true
        //         this._authenticated = true;

        //         // Store the user on the user service
        //         this._userService.user = response.user;

        //         // Return true
        //         return of(true);
        //     }),
        // );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; username: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { username: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }

    updateToken(): Observable<any> {
        return this.authService.refreshJWTToken({ RefreshToken: this.refreshToken }).pipe(
            switchMap((response: JWTTokenResponse) => {
                // Store the access token in the local storage
                this.accessToken = response.AccessToken;
                this.refreshToken = response.RefreshToken;
                // Set the authenticated flag to true
                this._authenticated = true;
                // Return a new observable with the response
                return of(response);
            })
        );
    }

    async updateTokenSync(): Promise<Observable<any>> {
        var response = await this.authService.refreshJWTToken({ RefreshToken: this.refreshToken}).toPromise();
        if (response)
        {
            this.accessToken = response.AccessToken;
            this.refreshToken = response.RefreshToken;
            this._authenticated = true;
        }
        
        return of(response);
    }
}
